
import type { WheelEvent } from 'react';

import { FAVES_PROD_BASE_URL } from './constants';

// A focused type=number input changes its value on mouse-wheel scroll, so
// scrolling past a field after typing silently alters the amount (e.g. 100 →
// 95). Attach as `onWheel={blurOnWheel}` so the wheel scrolls the page, not the
// field. Shared across the brand portal's numeric inputs.
export function blurOnWheel(e: WheelEvent<HTMLElement>): void {
    e.currentTarget.blur();
}

export enum WindowType {
    monthly = 0,
    lifetime = 1,
    yearToDate = 2,
}

export const USD_CURRENCY = 'USD';

export function formatCurrency(priceInCents: number) {
    const locale = navigator.language;
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: USD_CURRENCY,
    }).format(priceInCents / 100);
}


// Parse a free-typed dollar input into integer cents. Returns null when blank,
// non-numeric, or negative, so callers store null (unset / no override) rather
// than NaN. Inverse of centsToDollarsInput.
export function dollarsToCents(input: string): number | null {
    const trimmed = input.trim();
    if (trimmed === '') { return null; }
    const n = Number(trimmed);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}


// Format integer cents as a plain dollar string for a number input's value
// (empty string when null). Inverse of dollarsToCents.
export function centsToDollarsInput(cents: number | null): string {
    return cents === null ? '' : String(cents / 100);
}


export function formatNumber(value: number): string {
  return value.toLocaleString();
}

export function joinFullName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  return [firstName, lastName].filter(Boolean).join(' ');
}

/**
 * Compact currency for tight UI (cards, table cells). Cents in → "$1.5k" / "$2.34M" / "$842" out.
 * Returns '--' for null/undefined; pass `value ?? 0` if you want '$0' instead.
 */
export function formatCompactCurrency(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) {
    return '--';
  }
  const dollars = cents / 100;
  if (dollars >= 1_000_000) {
    return `$${(dollars / 1_000_000).toFixed(2)}M`;
  }
  if (dollars >= 1_000) {
    return `$${(dollars / 1_000).toFixed(1)}k`;
  }
  return `$${Math.round(dollars).toLocaleString()}`;
}

export function formatDuration(seconds: number): string {
  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) {
    return `${minutes}m`;
  }
  return `${hours}h ${minutes}m`;
}

export function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

export function formatRatioAsPercent(value: number | null | undefined, decimals: number = 2): string {
  if (value === null || value === undefined) {
    return '0%';
  }
  return `${(value * 100).toFixed(decimals)}%`;
}

export function expandMonth(shortMonth: string): string | null {
  const months: Record<string, string> = {
    jan: 'January',
    feb: 'February',
    mar: 'March',
    apr: 'April',
    may: 'May',
    jun: 'June',
    jul: 'July',
    aug: 'August',
    sep: 'September',
    oct: 'October',
    nov: 'November',
    dec: 'December',
  };

  const key = shortMonth.trim().toLowerCase();
  return months[key] ?? null; // returns null if abbreviation is not recognized
}

export type SocialConnectErrorCode =
    // Instagram
    | 'instagram_already_connected'
    | 'instagram_revoked_permissions'
    | 'instagram_key_error'
    | 'instagram_communication_error'
    | 'missing_code'
    | 'missing_scopes'
    | 'state_mismatch'
    | 'no_instagram_accounts'
    | 'instagram_business_portfolio_setup_invalid'
    | 'too_many_instagram_accounts'
    | 'rate_limited'
    | 'internal_error'
    // TikTok
    | 'tiktok_already_connected'
    | 'tiktok_revoked_permissions'
    | 'tiktok_communication_error'
    | 'tiktok_oauth_error'
    // Strava
    | 'strava_already_connected'
    | 'strava_revoked_permissions'
    | 'strava_communication_error'
    | 'strava_oauth_error'
    // YouTube
    | 'youtube_already_connected'
    | 'youtube_user_not_found'
    | 'youtube_revoked_permissions'
    | 'youtube_communication_error'
    | 'youtube_insufficient_reports'
    | 'youtube_no_channel';

export function mapErrorCodeToMessage(code: SocialConnectErrorCode): string {
    switch (code) {
        // Instagram
        case 'instagram_already_connected':
            return 'This Instagram account is already connected to another account.';

        case 'instagram_revoked_permissions':
            return 'We lost permission to access Instagram. Please reconnect to keep syncing metrics.';

        case 'instagram_key_error':
            return 'Something went wrong while connecting to Instagram. Please try again.';

        case 'instagram_communication_error':
            return 'We could not reach Instagram. Please try again in a moment.';

        case 'missing_code':
            return 'The connection was not completed. Please try again.';

        case 'missing_scopes':
            return 'Required permissions were not granted. Please try again.';

        case 'state_mismatch':
            return 'Your connection attempt expired. Please try again.';

        case 'no_instagram_accounts':
            return 'We couldn\'t find an Instagram professional account linked to your Facebook Page. Please review Step 7 in the connect guide, then retry.';

        case 'instagram_business_portfolio_setup_invalid':
            return 'Your Meta business portfolio setup is incomplete. Ensure your Facebook Page and Business Manager are connected and you have admin access, then try again.';

        case 'too_many_instagram_accounts':
            return 'Multiple Instagram accounts were found. Please select only one during connect.';

        case 'rate_limited':
            return 'Instagram is busy right now. Please try again in a minute.';

        case 'internal_error':
            return 'Something went wrong on our side. Please try again shortly.';

        // TikTok
        case 'tiktok_already_connected':
            return 'This TikTok account is already connected to another account.';

        case 'tiktok_revoked_permissions':
            return 'We lost permission to access TikTok. Please reconnect from your profile page.';

        case 'tiktok_communication_error':
            return 'We could not reach TikTok. Please try again in a moment.';

        case 'tiktok_oauth_error':
            return 'TikTok connection was not completed. Please try again.';
        // Strava
        case 'strava_already_connected':
            return 'This Strava account is already connected to another account.';
        case 'strava_revoked_permissions':
            return 'We lost permission to access Strava. Please reconnect from your profile page.';
        case 'strava_communication_error':
            return 'We could not reach Strava. Please try again in a moment.';
        case 'strava_oauth_error':
            return 'Strava connection was not completed. Please try again.';
        // YouTube
        case 'youtube_already_connected':
            return 'This YouTube channel is already connected to another account.';
        case 'youtube_user_not_found':
            return 'We could\'t find a YouTube channel associated with this Google account.';
        case 'youtube_revoked_permissions':
            return 'We lost permission to access YouTube. Please reconnect from your profile page.';
        case 'youtube_communication_error':
            return 'We could not reach YouTube. Please try again in a moment.';
        case 'youtube_insufficient_reports':
            return 'Required YouTube reporting permissions are missing. Please reconnect to enable full analytics access.';
        case 'youtube_no_channel':
            return 'This Google account doesn\'t have a YouTube channel. Please sign in with a Google account that has one, then try again.';
        default:
            return 'Something went wrong. Please try again.';
    }
}

export function isSocialConnectErrorCode(value: unknown): value is SocialConnectErrorCode {
  return typeof value === 'string';
}

export function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatShortDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatDateOrFallback(
  value: string | null | undefined,
  fallback: string = 'Never',
): string {
  if (!value) {
    return fallback;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDateTimeOrFallback(
  value: string | null | undefined,
  fallback: string = 'Never',
): string {
  if (!value) {
    return fallback;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleString('en-US', {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// Compact timestamp for chat bubbles (no year); '' on an unparseable date.
export function formatMessageTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return '';
  }
}

// Grow a chat composer textarea to fit its content, up to a cap (then it scrolls).
const COMPOSER_TEXTAREA_MAX_PX = 128;
export function autoGrowTextarea(el: HTMLTextAreaElement): void {
  el.style.height = 'auto';
  // Scrollbar only once content passes the cap; below it the box fits exactly.
  el.style.overflowY = el.scrollHeight > COMPOSER_TEXTAREA_MAX_PX ? 'auto' : 'hidden';
  el.style.height = `${Math.min(el.scrollHeight, COMPOSER_TEXTAREA_MAX_PX)}px`;
}

// Whether two ISO timestamps fall in the same wall-clock minute (used to avoid
// repeating a chat timestamp on consecutive messages). false on an unparseable date.
export function isSameMinute(isoA: string, isoB: string): boolean {
  const a = new Date(isoA).getTime();
  const b = new Date(isoB).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) { return false; }
  return Math.floor(a / 60000) === Math.floor(b / 60000);
}

export function daysSince(isoDate: string): number {
  const elapsedMs = Date.now() - new Date(isoDate).getTime();
  return Math.floor(elapsedMs / (1000 * 60 * 60 * 24));
}

export function pluralDays(days: number): string {
  return `${days} day${days === 1 ? '' : 's'}`;
}

export function normalizeWebsiteHref(
  website: string | null | undefined,
): string | undefined {
  if (!website) {
    return undefined;
  }

  const trimmed = website.trim();
  if (!trimmed) {
    return undefined;
  }

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  return `https://${trimmed}`;
}

export function makeProdUrl(pathname: string): URL {
  const normalizedPathname = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return new URL(normalizedPathname, FAVES_PROD_BASE_URL);
}

export function makeShopUrl(slug: string): URL {
  return makeProdUrl(`/@${slug}`);
}

export function makeProductUrl(slug: string, productPublicId: string): URL {
  return makeProdUrl(`/@${slug}/products/${productPublicId}`);
}

/**
 * Small DOM factory for simple element creation in vanilla TS entrypoints.
 * - `className` maps to `node.className`
 * - `text` maps to `node.textContent`
 * - `attrs` applies string attributes via `setAttribute`
 */
export function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts?: {
    className?: string;
    text?: string;
    attrs?: Record<string, string>;
  },
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  if (opts?.className) {
    node.className = opts.className;
  }

  if (typeof opts?.text === 'string') {
    node.textContent = opts.text;
  }

  if (opts?.attrs) {
    Object.entries(opts.attrs).forEach(([k, v]) => node.setAttribute(k, v));
  }

  return node;
}

type QueryValue = string | number | boolean | undefined | null;

export function makeUrl(
  base: string,
  params: Record<string, QueryValue>,
): string {
  const url = new URL(base, window.location.origin);

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
}

export function getYAxisWidth(maxValue: number, formatter?: (v: number) => string): number {
  const scaled = Number((maxValue * 10).toFixed(2));
  const label = formatter ? formatter(scaled) : `${scaled}`;
  return label.length * 10 + 10;
}

export type ImageWithUrlFallback = {
  thumbor_urls?: {
    jpeg?: Record<string, string>;
  };
  url?: string;
};

export function getPreferredImageUrl(
  image: ImageWithUrlFallback | null | undefined,
  preferredWidth: string = '320',
): string | undefined {
  const jpegUrls = image?.thumbor_urls?.jpeg;
  if (jpegUrls) {
    if (jpegUrls[preferredWidth]) {
      return jpegUrls[preferredWidth];
    }

    const sortedWidths = Object.keys(jpegUrls)
      .map((key) => Number(key))
      .filter((width) => Number.isFinite(width))
      .sort((a, b) => a - b);

    if (sortedWidths.length > 0) {
      const target = Number(preferredWidth);
      const nearestWidth = Number.isFinite(target)
        ? sortedWidths.reduce((nearest, current) =>
          Math.abs(current - target) < Math.abs(nearest - target) ? current : nearest)
        : sortedWidths[0];
      return jpegUrls[String(nearestWidth)];
    }

    return Object.values(jpegUrls)[0];
  }

  return image?.url;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Shared client-side email check (matches the footer subscribe + portal forms).
export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

const MIN_HANDLE_LENGTH = 3;
const MAX_HANDLE_LENGTH = 15;
const VALID_HANDLE_REGEX = /^[a-z0-9._-]*$/;

export default function validateSlug(slug: string): {valid: boolean; error?: string} {
  if (slug.length < MIN_HANDLE_LENGTH || slug.length > MAX_HANDLE_LENGTH) {
    return {
      valid: false,
      error: 'Must be between 3 and 15 characters long'
    };
  }

  if (!VALID_HANDLE_REGEX.test(slug)) {
    return {
      valid: false,
      error: 'Only lowercase letters, hyphens, and underscores are allowed'
    };
  }

  return {
    valid: true,
    error: undefined
  };
}
